import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faDownLeftAndUpRightToCenter } from '@fortawesome/pro-regular-svg-icons/faDownLeftAndUpRightToCenter';
import { faUpRightAndDownLeftFromCenter } from '@fortawesome/pro-regular-svg-icons/faUpRightAndDownLeftFromCenter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, ConfigProvider, Row, Spin, theme } from 'antd';
import type { AppointmentUpdatedEvent } from 'pos-client/components/customScheduler/types/schedulerTypes';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { type OrderTechniciansModalType } from 'pos-client/components/technicians/OrderTechniciansModal.tsx';
import { JobTechniciansProvider } from 'pos-client/contexts/JobTechniciansContext.tsx';
import { PosPageContent } from 'pos-client/layouts/page/PosPageContent.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { ScheduleBoardJobModel } from 'pos-shared/models/schedulingSchema.ts';

import { ScheduleBoardHeaderPanel } from './components/ScheduleBoardHeaderPanel.tsx';
import SchedulerComponent from './components/Scheduler.tsx';
import { WorkshopJobs } from './components/workshop/WorkshopJobs.tsx';
import { height } from './constants/boardConfig.ts';
import { useScheduleBoardContext } from './context/ScheduleBoardContext.tsx';
import { useConflictLanes } from './hooks/useConflictLanes.ts';
import { useJobsAndBlockedSlots } from './hooks/useJobsAndBlockedSlots.ts';
import { useMoveJob } from './hooks/useMoveJob.ts';
import { useNonTradingDates } from './hooks/useNonTradingDates.ts';
import { useResources } from './hooks/useResources.ts';
import { useTradingTimes } from './hooks/useTradingTimes.ts';
import {
  handleJobClick,
  handleJobDoubleClick,
  handleOpenModal,
  hideJobTooltip,
  useHandleJobAdd,
  useHandleJobDragStart,
  useHandleJobUpdating,
} from './utils/ScheduleBoardHandlers.ts';
import {
  getEstimatedTimeInLocationTimeZone,
  isBlockedSlot,
  useEvent,
} from './utils/ScheduleBoardUtils.ts';

import './ScheduleBoard.css';

const JobDetailsModal = lazy(() =>
  import('./components/modals/JobDetailsModal.tsx').then((m) => ({
    default: m.JobDetailsModal,
  }))
);
const OrderTechniciansModal = lazy(() =>
  import('pos-client/components/technicians/OrderTechniciansModal.tsx').then(
    (m) => ({ default: m.OrderTechniciansModal })
  )
);

export type ScheduleBoardMode = 'scheduler' | 'workshop';

interface ScheduleBoardProviderProps {
  mode: ScheduleBoardMode;
}

const ScheduleBoardProvider = ({ mode }: ScheduleBoardProviderProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const {
    selectedLocation,
    selectedDate,
    serviceTypeFilter,
    jobDetailsModalData,
    isNonTradingDate,
    editTechniciansModal,
    shouldPublishCrmEvent,
    setBoardData,
    setLaneConflicts,
    setJobDetailsModalData,
    setEditTechniciansModal,
    setSelectedOrderDetailsForAssignTechnician,
    setModalActiveKey,
  } = useScheduleBoardContext();

  const modalIsOpen = jobDetailsModalData !== null;
  const shellRef = useRef<HTMLDivElement>(null);
  const [userIsDragging, setUserIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  useEffect(() => {
    if (!isFullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen]);

  const { data: config, isLoading: configIsLoading } =
    trpc.application.getApplicationConfig.useQuery();

  const onSetEditTechniciansModal = useCallback(
    (technicianModalProps: OrderTechniciansModalType) =>
      setEditTechniciansModal(technicianModalProps),
    [setEditTechniciansModal]
  );

  const { resourceData, resourceDataIsLoading } = useResources();

  const assetIds = resourceData?.results
    ? resourceData?.results.map((asset) => asset.id)
    : [];

  const { jobsAndBlockedSlots, jobsAndBlockedSlotsLoading } =
    useJobsAndBlockedSlots(assetIds, config);

  const { moveJob, moveJobIsLoading } = useMoveJob();
  const { refetchTradingTimes } = useTradingTimes();
  const { nonTradingDates, refetchNonTradingDates } = useNonTradingDates();
  const { laneConflicts, laneConflictsLoading } = useConflictLanes(
    selectedLocation?.id as string,
    selectedLocation?.address?.timeZone as string,
    serviceTypeFilter
  );

  useEffect(() => {
    if (laneConflicts) {
      setLaneConflicts(laneConflicts);
    }
  }, [laneConflicts, setLaneConflicts]);

  useEffect(() => {
    if (selectedLocation?.openingHoursGroupId) {
      refetchTradingTimes();
      refetchNonTradingDates();
    }
  }, [
    selectedDate,
    selectedLocation,
    refetchTradingTimes,
    refetchNonTradingDates,
  ]);

  const boardIsLoading =
    jobsAndBlockedSlotsLoading || moveJobIsLoading || resourceDataIsLoading;

  useEffect(() => {
    if (resourceDataIsLoading) {
      setBoardData([]);
    }

    if (!resourceDataIsLoading && resourceData?.results?.length) {
      const mergedJobsAndLaneData = jobsAndBlockedSlots.map((job) => {
        const jobLaneId = job.laneId;
        const mappedLanes = resourceData?.results?.map((lane) => ({
          defaultDays: lane.defaultDays,
          laneId: lane.id,
        }));

        const lanesDefaultDays = mappedLanes?.find(
          (lane) => lane.laneId === jobLaneId
        );

        return {
          ...job,
          defaultDays: lanesDefaultDays?.defaultDays ?? [],
        };
      });

      setBoardData(mergedJobsAndLaneData);
    }
  }, [jobsAndBlockedSlots, resourceData, resourceDataIsLoading, setBoardData]);

  const handleJobUpdated = useEvent((e: AppointmentUpdatedEvent) => {
    const appointmentData =
      e.appointmentData as unknown as ScheduleBoardJobModel;
    const {
      orderId,
      scheduleId,
      jobId,
      bookingReference,
      laneId,
      serviceType,
      startDate,
      endDate,
    } = appointmentData;
    const locationTimeZone = selectedLocation?.address?.timeZone;
    const estimatedStartTime = getEstimatedTimeInLocationTimeZone(
      String(startDate),
      locationTimeZone
    );
    const estimatedFinishTime = getEstimatedTimeInLocationTimeZone(
      String(endDate),
      locationTimeZone
    );

    const resourceCode =
      resourceData?.results?.filter((resource) => resource.id === laneId)[0]
        .code ?? '';

    const resourceType =
      resourceData?.results?.filter((resource) => resource.id === laneId)[0]
        .type ?? '';

    moveJob({
      orderId: String(orderId),
      scheduleId: String(scheduleId),
      bookingReference: String(bookingReference),
      serviceType: (serviceType as 'Mobile' | 'Static') || 'Mobile',
      estimatedStartTime,
      estimatedFinishTime,
      resourceId: String(laneId),
      resourceCode,
      resourceType,
      shouldPublishCrmEvent,
    }).then((updatedjobDetailsModalData) => {
      if (Object.keys(updatedjobDetailsModalData).length) {
        setBoardData((jobs) => {
          return jobs.map((job) => {
            const currentJob = job as ScheduleBoardJobModel;
            const formatDisplaytime = (time: string) => {
              const checkForTimezone = (time.match(/-|\+/g) || []).length;
              return checkForTimezone === 3 ? time.slice(0, -6) : time;
            };

            const isMovedJob = jobId === currentJob.jobId;

            return {
              ...currentJob,
              ...(currentJob?.scheduleId && {
                scheduleId: isMovedJob
                  ? updatedjobDetailsModalData.scheduleId
                  : currentJob.scheduleId,
              }),
              ...(isMovedJob
                ? {
                    startDate: estimatedStartTime,
                    startDateDisplay: formatDisplaytime(estimatedStartTime),
                    endDate: estimatedFinishTime,
                    endDateDisplay: formatDisplaytime(estimatedFinishTime),
                  }
                : {}),
            };
          });
        });
        setUserIsDragging(false);
      }
    });
  });

  const handleCloseModal = () => {
    setJobDetailsModalData(null);
    setSelectedOrderDetailsForAssignTechnician({
      orderId: '',
      jobId: '',
    });
    setModalActiveKey('1');
  };

  const jobAddHandler = useHandleJobAdd(setJobDetailsModalData);
  const jobMoveHandler = useHandleJobDragStart(setUserIsDragging);
  const jobUpdatingHandler = useHandleJobUpdating();

  const renderBody = () => {
    if (mode === 'workshop') {
      return (
        <WorkshopJobs
          jobs={
            jobsAndBlockedSlots.filter(
              (s) => !isBlockedSlot(s)
            ) as ScheduleBoardJobModel[]
          }
          isLoading={jobsAndBlockedSlotsLoading}
        />
      );
    }

    if (configIsLoading) return null;

    return (
      <Row wrap={false}>
        <Col flex="1">
          <Spin
            tip={
              moveJobIsLoading && !jobsAndBlockedSlotsLoading
                ? `${t('scheduling.movingBooking')}...`
                : `${t('common.states.loading')}...`
            }
            size="large"
            spinning={boardIsLoading && !isNonTradingDate}
            style={{
              height,
              maxHeight: 'none',
            }}
          >
            <SchedulerComponent
              handleJobUpdating={jobUpdatingHandler}
              handleJobUpdated={handleJobUpdated}
              handleJobAdd={jobAddHandler}
              handleJobDragStart={jobMoveHandler}
              handleJobClick={(e) =>
                handleJobClick(e, (evt) =>
                  handleOpenModal(
                    evt,
                    setSelectedOrderDetailsForAssignTechnician,
                    setJobDetailsModalData
                  )
                )
              }
              handleJobDoubleClick={handleJobDoubleClick}
              hideJobTooltip={(e) => hideJobTooltip(e, modalIsOpen)}
              resourceData={resourceData}
              resourceDataIsLoading={resourceDataIsLoading}
              userIsDragging={userIsDragging}
            />
          </Spin>
        </Col>
      </Row>
    );
  };

  return (
    <>
      {editTechniciansModal.visible && (
        <JobTechniciansProvider
          initialTechnicians={editTechniciansModal.technicians}
        >
          <Suspense fallback={null}>
            <OrderTechniciansModal
              {...{
                ...{
                  ...editTechniciansModal,
                  ...{ serviceLocationId: selectedLocation?.id ?? '' },
                },
                onSetEditTechniciansModal,
              }}
            />
          </Suspense>
        </JobTechniciansProvider>
      )}
      {modalIsOpen && (
        <Suspense fallback={null}>
          <JobDetailsModal
            jobData={jobDetailsModalData}
            modalIsOpen={modalIsOpen}
            handleCloseModal={handleCloseModal}
            zIndex={isFullscreen ? 3000 : undefined}
          />
        </Suspense>
      )}

      <PosPageContent hideFooter>
        <ConfigProvider
          getPopupContainer={() =>
            (isFullscreen && shellRef.current) || document.body
          }
        >
        <div
          ref={shellRef}
          style={
            isFullscreen
              ? {
                  position: 'fixed',
                  inset: 0,
                  zIndex: 2000,
                  background: '#fff',
                  overflow: 'auto',
                  padding: 16,
                }
              : { position: 'relative' }
          }
        >
          <div style={{ position: 'relative' }}>
            {mode === 'scheduler' && (
              <Button
                type="text"
                aria-label={
                  isFullscreen
                    ? t('common.buttons.exitFullscreen')
                    : t('common.buttons.expand')
                }
                onClick={() => setIsFullscreen((v) => !v)}
                icon={
                  <FontAwesomeIcon
                    icon={
                      isFullscreen
                        ? faDownLeftAndUpRightToCenter
                        : faUpRightAndDownLeftFromCenter
                    }
                  />
                }
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 5,
                  color: token.colorPrimary,
                }}
              />
            )}
            {mode === 'workshop' ? (
              <>
                <PosCard flexProps={{ vertical: true, gap: 0 }}>
                  <ScheduleBoardHeaderPanel
                    mode="workshop"
                    nonTradingDates={nonTradingDates ?? []}
                    resourceDataIsLoading={resourceDataIsLoading}
                    laneConflictsLoading={laneConflictsLoading}
                    boardIsLoading={boardIsLoading}
                    hasLanes={!!resourceData?.results?.length}
                  />
                </PosCard>
                <div style={{ marginTop: 16 }}>{renderBody()}</div>
              </>
            ) : (
              <PosCard flexProps={{ vertical: true, gap: 0 }}>
                <ScheduleBoardHeaderPanel
                  mode="scheduler"
                  nonTradingDates={nonTradingDates ?? []}
                  resourceDataIsLoading={resourceDataIsLoading}
                  laneConflictsLoading={laneConflictsLoading}
                  boardIsLoading={boardIsLoading}
                  hasLanes={!!resourceData?.results?.length}
                />
                {renderBody()}
              </PosCard>
            )}
          </div>
        </div>
        </ConfigProvider>
      </PosPageContent>
    </>
  );
};

export default ScheduleBoardProvider;
